/**
 * FileDropOverlay — visual feedback when dragging files over the app window.
 *
 * Copy switches on `dragKind`, a best-effort MIME-based hint (see
 * `classifyDragPayload` in `fileDropClassifier.ts`): the web platform does not
 * expose dropped filenames/extensions during drag (only after `drop`), so a
 * reliable per-file classification pre-drop is impossible. When the OS
 * positively reports an import MIME (CSV/JSON/XLSX), we show the import
 * message; otherwise we show a combined message naming both actions, which
 * correctly covers `.sql`/`.txt`/`.tsv`/`.ndjson`/unrecognized-MIME drags.
 */

import { FileCode2, Upload } from 'lucide-react';

interface FileDropOverlayProps {
  isDragging: boolean;
  dragKind: 'import' | 'default' | 'upload';
  uploadLabel?: string;
}

export function FileDropOverlay({ isDragging, dragKind, uploadLabel }: FileDropOverlayProps) {
  if (!isDragging) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 pointer-events-none">
      <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-primary p-12">
        {dragKind === 'upload' ? (
          <>
            <Upload className="h-12 w-12 text-primary" />
            <p className="text-lg font-medium text-foreground">Drop to upload to {uploadLabel ?? 'Azure Storage'}</p>
          </>
        ) : dragKind === 'import' ? (
          <>
            <Upload className="h-12 w-12 text-primary" />
            <p className="text-lg font-medium text-foreground">Drop to import into a table</p>
          </>
        ) : (
          <>
            <FileCode2 className="h-12 w-12 text-primary" />
            <p className="text-lg font-medium text-foreground">
              Drop a file — SQL scripts open in the editor; CSV, Excel & JSON import into a table
            </p>
          </>
        )}
      </div>
    </div>
  );
}
